/**
 * GoogleAuthCallback — handles return from Emergent Google Auth service.
 *
 * URL contract (see /app/memory/ORACLE_ARCHITECTURE.md + Emergent Auth playbook):
 *   Google → https://auth.emergentagent.com/?redirect=<our_origin>/auth/callback/google
 *   Emergent → <our_origin>/auth/callback/google#session_id=XYZ
 *
 * We synchronously (during render, not useEffect) detect session_id and POST it
 * to our backend for exchange → member session token.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { socialApi, formatApiError } from "../../lib/authApi";
import { setMemberSession } from "../../hooks/useMemberAuth";
import { AuthShell, Notice, PrimaryBtn } from "../../components/auth/AuthShell";

/**
 * mode: "signin" | "link"
 *   - signin: exchange session_id → new member session
 *   - link:   attach Google provider to the currently signed-in member
 */
const GoogleAuthCallback = ({ mode = "signin" }) => {
  const nav = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const [state, setState] = useState({ status: "processing", error: null });

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    const sessionId = m ? decodeURIComponent(m[1]) : null;
    if (!sessionId) {
      setState({ status: "error",
                 error: "Missing sign-in token. Please try again from the login page." });
      return;
    }

    const nextParam = new URLSearchParams(location.search).get("next") || "/members";

    (async () => {
      try {
        // Link mode uses the current bearer; signin mode passes no bearer.
        const bearer = mode === "link"
          ? localStorage.getItem("archive_member_token")
          : null;
        const { data } = await socialApi.callback(
          { provider: "google", session_id: sessionId },
          bearer,
        );
        // Clear the fragment so refresh doesn't re-run
        window.history.replaceState({}, "", window.location.pathname + window.location.search);

        if (data.mode === "linked") {
          setState({ status: "linked", error: null });
          setTimeout(() => nav("/members/security"), 1200);
          return;
        }
        // signin / signup — establish session
        if (data.session_token) {
          await setMemberSession(data.session_token);
        }
        if (data.needs_profile_completion) {
          nav("/auth/finish-profile?next=" + encodeURIComponent(nextParam));
        } else {
          nav(nextParam);
        }
      } catch (e) {
        setState({
          status: "error",
          error: formatApiError(e?.response?.data?.detail) || "Google sign-in failed.",
        });
      }
    })();
  }, [nav, location.search, mode]);

  return (
    <AuthShell
      title={mode === "link" ? "Linking Google" : "Signing you in"}
      lead={mode === "link"
        ? "Attaching Google to your Archive account…"
        : "Establishing your session…"}
    >
      {state.status === "processing" && (
        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#A39E93]"
             data-testid="google-callback-processing">
          // WORKING…
        </div>
      )}
      {state.status === "linked" && (
        <Notice kind="success" data-testid="google-callback-linked">
          Google linked. Redirecting to security…
        </Notice>
      )}
      {state.status === "error" && (
        <>
          <Notice kind="error" data-testid="google-callback-error">{state.error}</Notice>
          <PrimaryBtn onClick={() => nav("/auth/login")} data-testid="google-callback-back">
            RETURN TO SIGN-IN
          </PrimaryBtn>
        </>
      )}
    </AuthShell>
  );
};

export default GoogleAuthCallback;
